import React, { useState, useEffect } from 'react';
import { 
  Cpu, TrendingUp, DollarSign, RefreshCw, 
  BarChart3, Zap, Clock, Activity
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LLMUsagePanel = ({ token }) => {
  const [usageData, setUsageData] = useState(null);
  const [todayUsage, setTodayUsage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [periodDays, setPeriodDays] = useState(30);

  const fetchUsageData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [usageRes, todayRes] = await Promise.all([
        axios.get(`${API_URL}/api/system/llm/usage?period_days=${periodDays}`, { headers }),
        axios.get(`${API_URL}/api/system/llm/today`, { headers })
      ]);
      
      if (usageRes.data.status === 'success') {
        setUsageData(usageRes.data);
      }
      if (todayRes.data.status === 'success') {
        setTodayUsage(todayRes.data);
      }
    } catch (err) {
      console.error('Error fetching LLM usage:', err);
      setError('Impossible de charger les données d\'utilisation');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchUsageData();
    }
  }, [token, periodDays]);

  if (isLoading) {
    return (
      <div className="bg-black border-2 border-blue-500/50 p-8 text-center">
        <RefreshCw className="w-12 h-12 text-blue-400 animate-spin mx-auto" />
        <p className="font-mono text-white/90 mt-4">Chargement des statistiques...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-black border-2 border-red-500/50 p-8 text-center">
        <p className="font-mono text-red-400">{error}</p>
        <button 
          onClick={fetchUsageData}
          className="mt-4 px-4 py-2 bg-red-500/20 border border-red-500 text-red-400 font-mono text-sm"
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="bg-black border-2 border-blue-500/50">
      <div className="bg-blue-900/30 px-4 py-3 border-b border-blue-500/50 flex items-center justify-between">
        <span className="font-mono text-blue-400 text-sm uppercase tracking-wider flex items-center gap-2">
          <Cpu className="w-4 h-4" />
          Consommation IA / LLM
        </span>
        <div className="flex items-center gap-4">
          <select
            value={periodDays}
            onChange={(e) => setPeriodDays(Number(e.target.value))}
            className="bg-black border border-blue-500/50 text-blue-400 font-mono text-sm px-2 py-1"
          >
            <option value={7}>7 jours</option>
            <option value={30}>30 jours</option>
            <option value={90}>90 jours</option>
          </select>
          <button
            onClick={fetchUsageData}
            className="text-blue-400 hover:text-white"
            title="Rafraîchir"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="p-4 space-y-6">
        {/* Today's Usage - Highlight */}
        {todayUsage && (
          <div className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 border border-blue-500/30 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-5 h-5 text-blue-400" />
              <span className="font-mono text-blue-400 text-sm uppercase">Aujourd'hui</span>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <p className="font-heading text-3xl text-white">{todayUsage.calls || 0}</p>
                <p className="font-mono text-white/80 text-xs">Appels</p>
              </div>
              <div className="text-center">
                <p className="font-heading text-3xl text-marine-cyan">{(todayUsage.tokens || 0).toLocaleString()}</p>
                <p className="font-mono text-white/80 text-xs">Tokens</p>
              </div>
              <div className="text-center">
                <p className="font-heading text-3xl text-marine-green">€{(todayUsage.cost_eur || 0).toFixed(4)}</p>
                <p className="font-mono text-white/80 text-xs">Coût</p>
              </div>
            </div>
          </div>
        )}

        {/* Total Usage Cards */}
        {usageData?.totals && (
          <div className="grid md:grid-cols-4 gap-4">
            <div className="bg-marine-slate/20 border border-marine-slate p-4">
              <div className="flex items-center gap-3">
                <Zap className="w-6 h-6 text-marine-cyan" />
                <div>
                  <p className="font-mono text-white/80 text-xs uppercase">Total Tokens</p>
                  <p className="font-heading text-marine-cyan text-2xl">{usageData.totals.tokens.toLocaleString()}</p>
                </div>
              </div>
            </div>
            <div className="bg-marine-green/10 border border-marine-green p-4">
              <div className="flex items-center gap-3">
                <DollarSign className="w-6 h-6 text-marine-green" />
                <div>
                  <p className="font-mono text-white/80 text-xs uppercase">Coût Total (€)</p>
                  <p className="font-heading text-marine-green text-2xl">€{usageData.totals.cost_eur.toFixed(2)}</p>
                </div>
              </div>
            </div>
            <div className="bg-marine-amber/10 border border-marine-amber p-4">
              <div className="flex items-center gap-3">
                <DollarSign className="w-6 h-6 text-marine-amber" />
                <div>
                  <p className="font-mono text-white/80 text-xs uppercase">Coût Total ($)</p>
                  <p className="font-heading text-marine-amber text-2xl">${usageData.totals.cost_usd.toFixed(2)}</p>
                </div>
              </div>
            </div>
            <div className="bg-purple-900/20 border border-purple-500 p-4">
              <div className="flex items-center gap-3">
                <Activity className="w-6 h-6 text-purple-400" />
                <div>
                  <p className="font-mono text-white/80 text-xs uppercase">Appels API</p>
                  <p className="font-heading text-purple-400 text-2xl">{usageData.totals.calls}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Usage by Model */}
        {usageData?.by_model && usageData.by_model.length > 0 && (
          <div>
            <h3 className="font-mono text-white/90 text-sm uppercase mb-3 flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Par Modèle IA
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-marine-slate/30">
                  <tr className="font-mono text-white/80 text-xs uppercase">
                    <th className="text-left p-3">Modèle</th>
                    <th className="text-left p-3">Fournisseur</th>
                    <th className="text-right p-3">Tokens</th>
                    <th className="text-right p-3">Appels</th>
                    <th className="text-right p-3">Coût €</th>
                    <th className="text-right p-3">Coût $</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-marine-slate/20">
                  {usageData.by_model.map((model, idx) => (
                    <tr key={idx} className="hover:bg-marine-slate/10">
                      <td className="p-3">
                        <p className="font-mono text-white text-sm">{model.name}</p>
                        <p className="font-mono text-white/40 text-xs">{model.model}</p>
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-1 text-xs font-mono uppercase rounded ${
                          model.provider === 'openai' ? 'bg-green-900/30 text-green-400' :
                          model.provider === 'anthropic' ? 'bg-orange-900/30 text-orange-400' :
                          model.provider === 'google' ? 'bg-blue-900/30 text-blue-400' :
                          'bg-gray-900/30 text-gray-400'
                        }`}>
                          {model.provider}
                        </span>
                      </td>
                      <td className="p-3 text-right font-mono text-marine-cyan text-sm">{model.tokens.toLocaleString()}</td>
                      <td className="p-3 text-right font-mono text-white/90 text-sm">{model.calls}</td>
                      <td className="p-3 text-right font-mono text-marine-green text-sm">€{model.cost_eur.toFixed(4)}</td>
                      <td className="p-3 text-right font-mono text-marine-amber text-sm">${model.cost_usd.toFixed(4)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Usage by Operation */}
        {usageData?.by_operation && usageData.by_operation.length > 0 && (
          <div>
            <h3 className="font-mono text-white/90 text-sm uppercase mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Par Type d'Opération
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {usageData.by_operation.map((op, idx) => (
                <div key={idx} className="bg-marine-slate/10 border border-marine-slate/30 p-3">
                  <p className="font-mono text-white text-sm capitalize mb-2">{op.operation.replace(/_/g, ' ')}</p>
                  <div className="flex justify-between items-center">
                    <span className="font-mono text-white/80 text-xs">{op.calls} appels</span>
                    <span className="font-mono text-marine-green text-sm">€{op.cost_eur.toFixed(4)}</span>
                  </div>
                  <div className="mt-2 bg-marine-slate/30 h-2 rounded">
                    <div 
                      className="bg-marine-cyan h-full rounded"
                      style={{ 
                        width: `${Math.min(100, (op.tokens / (usageData.totals?.tokens || 1)) * 100)}%` 
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Daily History Chart */}
        {usageData?.daily_history && usageData.daily_history.length > 0 && (
          <div>
            <h3 className="font-mono text-white/90 text-sm uppercase mb-3 flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Historique Journalier
            </h3>
            <div className="overflow-x-auto">
              <div className="flex items-end gap-1 h-32 min-w-[600px]">
                {usageData.daily_history.map((day, idx) => {
                  const maxCost = Math.max(...usageData.daily_history.map(d => d.cost_eur));
                  const height = maxCost > 0 ? (day.cost_eur / maxCost) * 100 : 0;
                  
                  return (
                    <div 
                      key={idx} 
                      className="flex-1 flex flex-col items-center group relative"
                    >
                      <div 
                        className="w-full bg-marine-cyan/60 hover:bg-marine-cyan transition-colors"
                        style={{ height: `${Math.max(4, height)}%` }}
                        title={`${day.date}: €${day.cost_eur.toFixed(4)} | ${day.tokens} tokens | ${day.calls} appels`}
                      />
                      <span className="font-mono text-white/30 text-xs mt-1 rotate-45 origin-left">
                        {day.date.slice(5)}
                      </span>
                      
                      {/* Tooltip */}
                      <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-black border border-marine-cyan/50 p-2 text-xs opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">
                        <p className="font-mono text-marine-cyan">{day.date}</p>
                        <p className="font-mono text-white/90">€{day.cost_eur.toFixed(4)}</p>
                        <p className="font-mono text-white/80">{day.tokens} tokens</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Pricing Info */}
        {usageData?.pricing_info && (
          <details className="mt-4">
            <summary className="font-mono text-white/80 text-xs uppercase cursor-pointer hover:text-white/90">
              Tarifs par modèle
            </summary>
            <div className="mt-3 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {Object.entries(usageData.pricing_info).map(([id, info]) => (
                <div key={id} className="bg-marine-slate/10 border border-marine-slate/20 p-3 text-xs">
                  <p className="font-mono text-white font-bold mb-1">{info.name}</p>
                  <p className="font-mono text-white/80">Input: ${info.input_per_1m}/1M tokens</p>
                  <p className="font-mono text-white/80">Output: ${info.output_per_1m}/1M tokens</p>
                </div>
              ))}
            </div>
          </details>
        )}

        {/* No data message */}
        {(!usageData?.by_model || usageData.by_model.length === 0) && (
          <div className="p-8 text-center">
            <Cpu className="w-12 h-12 text-blue-400/30 mx-auto mb-4" />
            <p className="font-mono text-white/80">Aucune donnée d'utilisation LLM pour cette période</p>
            <p className="font-mono text-white/30 text-xs mt-2">
              Les données apparaîtront après utilisation des fonctionnalités IA
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LLMUsagePanel;
